/* ==========================================================================
   Young Grandpa - photo strip drag/auto-scroll
   --------------------------------------------------------------------------
   Unlike assets/yg-ugc-carousel.js, which drives a transform on a track,
   this one scrolls a real overflow-x container. That is deliberate: the
   design reference (.design-refs/scuffers-photo-strip-drag-detail.png)
   shows a native horizontal scrollbar under the strip, so the strip has to
   BE a scroller - a transformed track has no scrollbar to show, and no
   native touch momentum or trackpad/shift-wheel scrolling either.

   The seamless loop is the UGC carousel's clone trick wrapped on scrollLeft
   instead of a transform: the rendered blocks are cloned and scrollLeft is
   snapped by one set's width whenever it crosses either end of that band.
   The number of clones is computed from the viewport rather than fixed at
   one, because wrapping needs a whole viewport of runway past the wrap
   point - a handful of tiles on a wide screen would otherwise hit the
   browser's hard scrollLeft clamp before the wrap could fire. Because a
   wrap is a jump in scrollLeft, it has to happen while no smooth scroll is
   in flight, which is why every path here sets scrollLeft directly rather
   than using scrollTo({behavior:'smooth'}).

   Auto-scroll advances by a sub-pixel amount per frame, accumulated in a
   float (`drift`) and only committed to scrollLeft once it crosses a whole
   pixel - scrollLeft is integer-quantised, so adding 0.4 to it every frame
   would otherwise round to zero and never move.

   Drag is direct manipulation: scrollLeft tracks the pointer 1:1 from the
   grab point. Release hands off to a short momentum decay, then auto-scroll
   resumes after a pause. Vertical intent is detected before claiming the
   gesture (same INTENT_THRESHOLD approach as the UGC carousel) so a
   mostly-vertical touch still scrolls the page.
   ========================================================================== */

(function () {
  'use strict';

  var ROOT = '[data-yg-photo-strip]';
  var INTENT_THRESHOLD = 10;
  var DRAG_RELEASE_PAUSE_MS = 1600;
  var VELOCITY_DECAY = 0.94;
  var VELOCITY_FLOOR = 0.05;
  var WHEEL_INTENT = 1;
  /* section setting 1-20 maps onto this many px/frame */
  var SPEED_UNIT = 0.14;

  var instances = Object.create(null);
  var REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)');

  function stripEditorAttrs(root) {
    var els = root.querySelectorAll('[data-shopify-editor-block-id], [data-block-id]');
    for (var i = 0; i < els.length; i++) {
      var attrs = Array.prototype.slice.call(els[i].attributes);
      for (var j = 0; j < attrs.length; j++) {
        var name = attrs[j].name;
        if (name.indexOf('data-shopify') === 0 || name === 'data-block-id') {
          els[i].removeAttribute(name);
        }
      }
    }
  }

  /* Duplicates the server-rendered tiles so scrollLeft can wrap without a
     visible end. One copy is not always enough: wrapping needs a full
     viewport of runway PAST the wrap point, so a set narrower than the
     viewport (few tiles, wide screen) needs several copies. Guarded by a
     data attribute - resize/recalc must not re-clone. Clones are hidden
     from assistive tech and untabbable.
     Returns the width of one original set, which is the wrap period. */
  function ensureClones(track, viewportWidth) {
    var originals = Array.prototype.slice.call(track.children);
    if (!originals.length) return 0;

    if (track.dataset.psCloned === 'true') {
      return parseFloat(track.dataset.psSegment) || 0;
    }

    var setWidth = track.scrollWidth;
    /* +1 so there is always at least one whole spare set beyond the runway */
    var copies = setWidth > 0 ? Math.ceil(viewportWidth / setWidth) + 1 : 1;
    var originalCount = originals.length;

    for (var c = 0; c < copies; c++) {
      for (var i = 0; i < originals.length; i++) {
        var clone = originals[i].cloneNode(true);
        stripEditorAttrs(clone);
        clone.dataset.psClone = 'true';
        clone.setAttribute('aria-hidden', 'true');
        var focusables = clone.querySelectorAll('[tabindex="0"], a, button');
        for (var k = 0; k < focusables.length; k++) {
          focusables[k].setAttribute('tabindex', '-1');
        }
        track.appendChild(clone);
      }
    }

    track.dataset.psCloned = 'true';
    track.dataset.psSets = String(copies + 1);
    track.dataset.psOriginals = String(originalCount);
    /* Measure the period AFTER cloning, as the offset between a tile and its
       first copy. Tiles overlap via a negative margin on
       `.yg-ps__tile + .yg-ps__tile`, and that margin does not exist before
       the first clone is appended - so the pre-clone scrollWidth is one
       margin wider than the true repeat distance, and wrapping on it would
       nudge the strip sideways by that much on every lap. */
    track.dataset.psSegment = String(measureSegment(track));
    return parseFloat(track.dataset.psSegment);
  }

  /**
   * True repeat distance: the left offset between the first original tile
   * and its first clone. Falls back to an even division of scrollWidth if
   * the children are not where we expect.
   *
   * @param {Element} track
   * @returns {number}
   */
  function measureSegment(track) {
    var originalCount = parseFloat(track.dataset.psOriginals) || 0;
    var first = track.children[0];
    var firstClone = track.children[originalCount];
    if (first && firstClone) {
      var period = firstClone.offsetLeft - first.offsetLeft;
      if (period > 0) return period;
    }
    var sets = parseFloat(track.dataset.psSets) || 2;
    return track.scrollWidth / sets;
  }

  function attach(root) {
    var viewport = root.querySelector('[data-yg-ps-viewport]');
    var track = root.querySelector('[data-yg-ps-track]');
    if (!viewport || !track || !track.children.length) return null;

    var destroyed = false;
    var rafId = null;

    var speed = (parseFloat(root.dataset.autoscroll) || 0) * SPEED_UNIT;
    var segment = ensureClones(track, Math.max(viewport.clientWidth, window.innerWidth));
    var drift = 0;
    var dragging = false;
    var startX = 0;
    var startY = 0;
    var startScroll = 0;
    var velocity = 0;
    var lastX = 0;
    var lastT = 0;
    var idleTimer = null;
    var paused = false;
    var intent = 'pending';

    function measure() {
      if (!segment) segment = parseFloat(track.dataset.psSegment) || 0;
    }

    /* Keeps scrollLeft inside (0, segment). Both wraps land strictly INSIDE
       the band rather than on its edge - landing exactly on 0 or on segment
       would re-trigger the opposite wrap on the very next frame and
       ping-pong. scrollLeft can never go negative (the browser clamps it),
       so the low edge is where a backward drag or drift arrives. */
    function wrap() {
      if (segment <= 0) return;
      /* Clone count was sized against the viewport at init. If the window is
         later widened past that, there is no longer a whole set of runway
         past the wrap point - wrapping there would strand scrollLeft against
         the browser's clamp, so fall back to behaving as a plain finite
         scroller until the next load re-clones. */
      if (viewport.scrollWidth - viewport.clientWidth < segment) return;
      var x = viewport.scrollLeft;
      if (x >= segment) {
        viewport.scrollLeft = x - segment + 1;
      } else if (x <= 0) {
        viewport.scrollLeft = segment - 1;
      }
    }

    function frame() {
      if (destroyed) return;

      if (!dragging) {
        if (Math.abs(velocity) > VELOCITY_FLOOR) {
          drift += velocity;
          velocity *= VELOCITY_DECAY;
        } else if (!paused && speed > 0 && !REDUCED.matches) {
          drift += speed;
        }

        if (Math.abs(drift) >= 1) {
          var whole = drift > 0 ? Math.floor(drift) : Math.ceil(drift);
          viewport.scrollLeft += whole;
          drift -= whole;
          wrap();
        }
      }
      rafId = requestAnimationFrame(frame);
    }

    function pauseFor(ms) {
      paused = true;
      clearTimeout(idleTimer);
      idleTimer = setTimeout(function () {
        paused = false;
      }, ms);
    }

    function onPointerDown(event) {
      /* A native scrollbar lives inside the viewport's border box but below
         its clientHeight: grabbing it must stay a real scrollbar drag, not
         get hijacked into a scrub. offsetY is relative to event.target -
         whichever tile happens to be under the cursor - so the test has to
         be done against the viewport's own box. */
      if (event.clientY - viewport.getBoundingClientRect().top > viewport.clientHeight) return;
      if (event.target.closest && event.target.closest('a, button')) return;

      dragging = true;
      intent = 'pending';
      velocity = 0;
      drift = 0;
      startX = event.clientX;
      startY = event.clientY;
      startScroll = viewport.scrollLeft;
      lastX = event.clientX;
      lastT = performance.now();
      viewport.classList.add('is-dragging');
      clearTimeout(idleTimer);
      paused = true;
    }

    function resolveIntent(dx, dy, event) {
      if (intent !== 'pending') return intent === 'horizontal';
      if (Math.abs(dx) < INTENT_THRESHOLD && Math.abs(dy) < INTENT_THRESHOLD) return false;

      intent = Math.abs(dx) > Math.abs(dy) ? 'horizontal' : 'vertical';
      if (intent === 'horizontal') {
        viewport.setPointerCapture(event.pointerId);
        return true;
      }

      dragging = false;
      viewport.classList.remove('is-dragging');
      clearTimeout(idleTimer);
      paused = false;
      return false;
    }

    function onPointerMove(event) {
      if (!dragging) return;
      var dx = event.clientX - startX;
      var dy = event.clientY - startY;
      if (!resolveIntent(dx, dy, event)) return;

      event.preventDefault();
      viewport.scrollLeft = startScroll - dx;
      wrap();

      var now = performance.now();
      var dt = now - lastT || 16;
      velocity = ((lastX - event.clientX) / dt) * 16;
      lastX = event.clientX;
      lastT = now;
    }

    function endDrag(event) {
      if (!dragging) return;
      dragging = false;
      viewport.classList.remove('is-dragging');
      if (event && event.pointerId != null && viewport.hasPointerCapture(event.pointerId)) {
        viewport.releasePointerCapture(event.pointerId);
      }
      pauseFor(DRAG_RELEASE_PAUSE_MS);
    }

    /* Native wheel/trackpad and scrollbar drags move scrollLeft without any
       of the handlers above firing, so the wrap has to be re-checked from
       the scroll event too. */
    function onScroll() {
      if (dragging) return;
      wrap();
    }

    /* Only a horizontal gesture is scrubbing the strip. A vertical wheel that
       happens to land on it is the page scrolling past, and must leave the
       drift alone. Shift+wheel is horizontal too, but some browsers report it
       on deltaY. */
    function onWheel(event) {
      if (dragging) return;
      var horizontal = event.shiftKey
        ? Math.max(Math.abs(event.deltaX), Math.abs(event.deltaY))
        : Math.abs(event.deltaX);
      var vertical = event.shiftKey ? 0 : Math.abs(event.deltaY);
      if (horizontal < WHEEL_INTENT || horizontal <= vertical) return;
      pauseFor(DRAG_RELEASE_PAUSE_MS);
    }

    function onResize() {
      measure();
      wrap();
    }

    viewport.addEventListener('pointerdown', onPointerDown);
    viewport.addEventListener('pointermove', onPointerMove);
    viewport.addEventListener('pointerup', endDrag);
    viewport.addEventListener('pointercancel', endDrag);
    viewport.addEventListener('scroll', onScroll, { passive: true });
    viewport.addEventListener('wheel', onWheel, { passive: true });
    window.addEventListener('resize', onResize);

    measure();
    /* Start one whole set in, so the very first backward drag has runway
       instead of hitting the browser's hard clamp at 0. */
    viewport.scrollLeft = segment > 0 ? segment - 1 : 0;
    /* Images are lazy and mostly unsized until they decode, so the segment
       measured at init can be short; re-measuring once layout settles keeps
       the wrap period honest. */
    var recalcTimer = setTimeout(function () {
      if (destroyed) return;
      var settled = measureSegment(track);
      if (settled > 0 && Math.abs(settled - segment) > 2) {
        segment = settled;
        track.dataset.psSegment = String(segment);
        viewport.scrollLeft = segment - 1;
      }
      wrap();
    }, 400);
    rafId = requestAnimationFrame(frame);

    return {
      destroy: function () {
        destroyed = true;
        if (rafId) cancelAnimationFrame(rafId);
        clearTimeout(idleTimer);
        clearTimeout(recalcTimer);
        viewport.removeEventListener('pointerdown', onPointerDown);
        viewport.removeEventListener('pointermove', onPointerMove);
        viewport.removeEventListener('pointerup', endDrag);
        viewport.removeEventListener('pointercancel', endDrag);
        viewport.removeEventListener('scroll', onScroll);
        viewport.removeEventListener('wheel', onWheel);
        window.removeEventListener('resize', onResize);
      },
    };
  }

  function init() {
    var roots = document.querySelectorAll(ROOT);
    for (var i = 0; i < roots.length; i++) {
      var root = roots[i];
      var sectionId = root.dataset.sectionId;
      if (!sectionId || instances[sectionId]) continue;

      var controller = attach(root);
      if (controller) instances[sectionId] = controller;
    }
  }

  document.addEventListener('shopify:section:load', init);
  document.addEventListener('shopify:section:unload', function (event) {
    var sectionId = event.detail && event.detail.sectionId;
    if (!sectionId || !instances[sectionId]) return;
    instances[sectionId].destroy();
    delete instances[sectionId];
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
